import { coreTypes } from "@core/core-types.di";
import { HttpFailedRequestError } from "@core/data/infrastructures/http/errors/http-failed-request.error";
import type { Http } from "@core/data/infrastructures/http/http";
import { HttpErrorCodeEnum } from "@core/data/infrastructures/http/http-error-response";
import { FallbackError } from "@core/domain/errors/fallback.error";
import { ValidationError } from "@core/domain/errors/validation.error";
import { Either } from "@core/domain/types/either";
import { inject, injectable } from "inversify";

@injectable()
export class BudgetTemplateDataSource {
    private static readonly BASE_ROUTE = "budgets";

    constructor(
        @inject(coreTypes.infrastructure.Http)
        private readonly http: Http,
    ) { }

    async getAllTemplates(): Promise<Either<FallbackError, any[]>> {
        const response = await this.http.get<any[]>(`${BudgetTemplateDataSource.BASE_ROUTE}/templates/`);
        return response
            .mapLeft(() => new FallbackError())
            .map((response) => response.data);
    }

    async createBudgetFromTemplate(createBudgetFromTemplate: any): Promise<Either<ValidationError | FallbackError, any>> {
        const response = await this.http.post<any>(`${BudgetTemplateDataSource.BASE_ROUTE}/create_from_template/`, createBudgetFromTemplate);
        return response
            .mapLeft((error) => {
                if (error instanceof HttpFailedRequestError && error.errorCode === HttpErrorCodeEnum.GenericError)
                    return new ValidationError(error.data);
                return new FallbackError();
            })
            .map((response) => response.data);
    }

    async markAsTemplate(markAsTemplate: any): Promise<Either<ValidationError | FallbackError, any>> {
        const response = await this.http.post<any>(`${BudgetTemplateDataSource.BASE_ROUTE}/mark_as_template/`, markAsTemplate);
        return response
            .mapLeft((error) => {
                if (error instanceof HttpFailedRequestError && error.errorCode === HttpErrorCodeEnum.GenericError)
                    return new ValidationError(error.data);
                return new FallbackError();
            })
            .map((response) => response.data);
    }

    async createTemplate(templateData: any): Promise<Either<ValidationError | FallbackError, any>> {
        const response = await this.http.post<any>(`${BudgetTemplateDataSource.BASE_ROUTE}/`, templateData);
        return response
            .mapLeft((error) => {
                if (error instanceof HttpFailedRequestError && error.errorCode === HttpErrorCodeEnum.GenericError)
                    return new ValidationError(error.data);
                return new FallbackError();
            })
            .map((response) => response.data);
    }

    async createTemplateFromTemplate(payload: {
        template_id: number;
        name: string;
        description: string;
    }): Promise<Either<ValidationError | FallbackError, any>> {
        const response = await this.http.post<any>(
            `${BudgetTemplateDataSource.BASE_ROUTE}/create_template_from_template/`,
            payload,
        );
        return response
            .mapLeft((error) => {
                if (error instanceof HttpFailedRequestError && error.errorCode === HttpErrorCodeEnum.GenericError)
                    return new ValidationError(error.data);
                return new FallbackError();
            })
            .map((response) => response.data);
    }
}
